import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, Grid, IconButton, MenuItem, Tab, Tabs,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Tooltip, Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import * as clienteService from "../services/clienteService";
import TruncatedCellText from "./TruncatedCellText";
import ImprimirContratoPanel from "./ImprimirContratoPanel";
import ENV from "../../../config/env";

const SubHeaderCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  fontSize: "0.72rem",
  backgroundColor: theme.palette.mode === "dark"
    ? theme.palette.grey[900]
    : theme.palette.grey[50],
  color: theme.palette.text.secondary,
}));

const DIALOG_COMPACT_SX = {
  pt: 1,
  "& .MuiTextField-root .MuiInputBase-root:not(.MuiInputBase-multiline)": {
    minHeight: 36,
  },
  "& .MuiInputBase-input": {
    fontSize: "0.82rem",
  },
  "& .MuiInputBase-input:not(textarea)": {
    py: "7px",
  },
  "& .MuiSelect-select": {
    py: "7px",
  },
  "& .MuiInputLabel-root": {
    fontSize: "0.82rem",
  },
};

const CONTRATO_INI = {
  estado: "Vigente",
  plazo_mes: "",
  garantia: "Letra de Cambio",
  num_letra: "",
  monto_letra: "",
  nombre_letra: "",
  fecha_letra: "",
  fecha_inicio: "",
  fecha_finalizacion: "",
  tasita: "Si",
  fecha_tasita: "",
  observacion: "",
  portatil: 0,
  pre_standar_m: 0,
  inter_acc: 0,
  pre_vent_radio: 0,
  pre_inter: 0,
  movil: 0,
  base: 0,
  frecuencia: 0,
  accesorios: 0,
  pre_vent_mdem: 0,
  Pre_servicio: 0,
  venta_accesorios: 0,
  venta_internacional: 0,
  venta_inter_acc: 0,
};

const ESTADOS_CONTRATO = ["Vigente", "Terminado"];
const GARANTIAS = [
  "Otro",
  "Bienes Habidos y por Haber",
  "Letra de Cambio",
  "Boleta de Garantia",
  "Ninguna",
];

const PRECIO_FIELDS = {
  TKR: [
    ["portatil", "Portatil"],
    ["pre_vent_radio", "Precio venta"],
    ["movil", "Movil"],
    ["frecuencia", "Frecuencia"],
    ["base", "Base"],
  ],
  AVL: [
    ["pre_standar_m", "Precio servicio estandar"],
    ["pre_vent_mdem", "Precio venta"],
    ["accesorios", "Precio servicio con accesorios"],
    ["venta_accesorios", "Precio venta con accesorios"],
    ["pre_inter", "Precio servicio internacional"],
    ["venta_internacional", "Precio venta internacional"],
    ["inter_acc", "Precio servicio internacional con accesorios"],
    ["venta_inter_acc", "Precio venta internacional con accesorios"],
    ["Pre_servicio", "Precio servicio"],
  ],
};

const ALL_PRECIO_FIELDS = Object.values(PRECIO_FIELDS).flat();

const normalizeList = (data) => {
  const payload = data?.data ?? data;
  return Array.isArray(payload) ? payload : [];
};

const toDateInput = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime()) || date.getFullYear() <= 1900) return "";
  return date.toISOString().slice(0, 10);
};

const formatDate = (value) => toDateInput(value) || "—";

const toNumber = (value) => {
  if (value === "" || value === null || value === undefined) return 0;
  return Number(value) || 0;
};

const getTipoServicio = (servicio) => {
  const nombre = (
    servicio?.nombre_servicio ||
    servicio?.servicio_nombre ||
    servicio?.nombre ||
    ""
  ).toUpperCase();

  if (nombre.includes("TKR")) return "TKR";
  if (nombre.includes("AVL")) return "AVL";
  if (Number(servicio?.cod_servicio) === 1) return "TKR";
  if (Number(servicio?.cod_servicio) === 2) return "AVL";
  return "";
};

const formatTamano = (bytes) => {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const ContratosServicioDialog = ({ open, cliente, servicio, onClose }) => {
  const [tab, setTab] = useState(0);
  const [contratos, setContratos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [finishingId, setFinishingId] = useState(null);
  const [error, setError] = useState(null);
  const [formError, setFormError] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(CONTRATO_INI);
  const tipoServicio = getTipoServicio(servicio);
  const precioFields = PRECIO_FIELDS[tipoServicio] || [];

  const [archivos, setArchivos] = useState([]);
  const [archivosLoading, setArchivosLoading] = useState(false);
  const [archivosError, setArchivosError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [deletingArchivoId, setDeletingArchivoId] = useState(null);
  const fileInputRef = useRef(null);

  const cargarContratos = useCallback(async () => {
    if (!cliente?.cod_cliente || !servicio?.cod_servicio_cliente) return;

    setLoading(true);
    setError(null);
    try {
      const { data } = await clienteService.getContratosServicio(
        cliente.cod_cliente,
        servicio.cod_servicio_cliente,
      );
      setContratos(normalizeList(data));
    } catch (err) {
      setError(err?.response?.data?.message || "No se pudieron cargar los contratos");
      setContratos([]);
    } finally {
      setLoading(false);
    }
  }, [cliente?.cod_cliente, servicio?.cod_servicio_cliente]);

  const cargarArchivos = useCallback(async () => {
    if (!cliente?.cod_cliente || !servicio?.cod_servicio_cliente) return;

    setArchivosLoading(true);
    setArchivosError(null);
    try {
      const { data } = await clienteService.getContratoArchivos(
        cliente.cod_cliente,
        servicio.cod_servicio_cliente,
      );
      setArchivos(normalizeList(data));
    } catch (err) {
      setArchivosError(err?.response?.data?.message || "No se pudieron cargar los archivos");
      setArchivos([]);
    } finally {
      setArchivosLoading(false);
    }
  }, [cliente?.cod_cliente, servicio?.cod_servicio_cliente]);

  useEffect(() => {
    if (open) {
      setTab(0);
      setEditing(null);
      setForm(CONTRATO_INI);
      setFormError(null);
      setArchivosError(null);
      cargarContratos();
      cargarArchivos();
    }
  }, [open, cargarContratos, cargarArchivos]);

  const handleSeleccionarArchivos = () => fileInputRef.current?.click();

  const handleArchivosElegidos = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;

    setUploading(true);
    setArchivosError(null);
    try {
      await clienteService.uploadContratoArchivos(
        cliente.cod_cliente,
        servicio.cod_servicio_cliente,
        files,
      );
      await cargarArchivos();
    } catch (err) {
      setArchivosError(err?.response?.data?.message || "No se pudieron subir los archivos");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleEliminarArchivo = async (archivo) => {
    if (!window.confirm(`¿Eliminar "${archivo.nombre_original}"?`)) return;

    setDeletingArchivoId(archivo.id);
    try {
      await clienteService.deleteContratoArchivo(
        cliente.cod_cliente,
        servicio.cod_servicio_cliente,
        archivo.id,
      );
      await cargarArchivos();
    } catch (err) {
      setArchivosError(err?.response?.data?.message || "No se pudo eliminar el archivo");
    } finally {
      setDeletingArchivoId(null);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleNew = () => {
    setEditing(null);
    setForm(CONTRATO_INI);
    setFormError(null);
  };

  const handleEdit = (contrato) => {
    setEditing(contrato);
    setForm({
      ...CONTRATO_INI,
      ...contrato,
      estado: contrato.estado || "Vigente",
      plazo_mes: contrato.plazo_mes || "",
      garantia: contrato.garantia || "Otro",
      fecha_letra: toDateInput(contrato.fecha_letra),
      fecha_inicio: toDateInput(contrato.fecha_inicio),
      fecha_finalizacion: toDateInput(contrato.fecha_finalizacion),
      tasita: contrato.tasita || "No",
      fecha_tasita: toDateInput(contrato.fecha_tasita),
      observacion: contrato.observacion || "",
    });
    setFormError(null);
  };

  const buildPayload = () => ({
    ...form,
    estado: form.estado || "Vigente",
    plazo_mes: form.plazo_mes === "" ? "" : String(form.plazo_mes),
    garantia: form.garantia || "Otro",
    num_letra: (form.num_letra || "").trim(),
    monto_letra: (form.monto_letra || "").trim(),
    nombre_letra: (form.nombre_letra || "").trim(),
    fecha_letra: form.fecha_letra || "",
    fecha_inicio: form.fecha_inicio || "",
    fecha_finalizacion: form.fecha_finalizacion || "",
    tasita: form.tasita || "No",
    fecha_tasita: form.fecha_tasita || "",
    observacion: (form.observacion || "").trim(),
    ...Object.fromEntries(ALL_PRECIO_FIELDS.map(([name]) => [name, toNumber(form[name])])),
  });

  const handleSaveContrato = async () => {
    if (!form.plazo_mes) {
      setFormError("El plazo en meses es requerido");
      return;
    }

    setSaving(true);
    setFormError(null);
    try {
      const payload = buildPayload();

      if (editing?.id_contrato) {
        await clienteService.updateContratoServicio(
          cliente.cod_cliente,
          servicio.cod_servicio_cliente,
          editing.id_contrato,
          payload,
        );
      } else {
        await clienteService.createContratoServicio(
          cliente.cod_cliente,
          servicio.cod_servicio_cliente,
          payload,
        );
      }

      await cargarContratos();
      handleNew();
    } catch (err) {
      setFormError(err?.response?.data?.message || "No se pudo guardar el contrato");
    } finally {
      setSaving(false);
    }
  };

  const handleFinalizar = async (contrato) => {
    if (!contrato?.id_contrato) return;
    if (!window.confirm("¿Finalizar este contrato?")) return;

    setFinishingId(contrato.id_contrato);
    setFormError(null);
    try {
      await clienteService.deleteContratoServicio(
        cliente.cod_cliente,
        servicio.cod_servicio_cliente,
        contrato.id_contrato,
      );
      await cargarContratos();
      if (editing?.id_contrato === contrato.id_contrato) handleNew();
    } catch (err) {
      setFormError(err?.response?.data?.message || "No se pudo finalizar el contrato");
    } finally {
      setFinishingId(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Contratos - Servicio #{servicio.cod_servicio_cliente}</DialogTitle>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 3 }}>
        <Tab label="Subir Contrato" />
        <Tab label="Registro de Contrato" />
        <Tab label="Imprimir Contrato" />
      </Tabs>
      <Divider />
      <DialogContent>
        {tab === 2 && <ImprimirContratoPanel codServicioCliente={servicio.cod_servicio_cliente} />}
        {tab === 0 && (
          <Box sx={{ pt: 1 }}>
            {archivosError && <Alert severity="error" sx={{ mb: 1.5 }}>{archivosError}</Alert>}

            <input
              ref={fileInputRef}
              type="file"
              multiple
              hidden
              onChange={handleArchivosElegidos}
            />
            <Box
              sx={{
                border: "2px dashed",
                borderColor: "divider",
                borderRadius: 1,
                p: 3,
                textAlign: "center",
                cursor: uploading ? "default" : "pointer",
                mb: 2,
              }}
              onClick={uploading ? undefined : handleSeleccionarArchivos}
            >
              {uploading ? (
                <CircularProgress size={28} />
              ) : (
                <>
                  <CloudUploadIcon sx={{ fontSize: 36, color: "text.secondary" }} />
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    Click para elegir uno o varios archivos
                  </Typography>
                </>
              )}
            </Box>

            {archivosLoading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}>
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">Cargando archivos...</Typography>
              </Box>
            ) : !archivos.length ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                Sin archivos subidos para este servicio
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <SubHeaderCell>Archivo</SubHeaderCell>
                      <SubHeaderCell>Tamaño</SubHeaderCell>
                      <SubHeaderCell>Usuario</SubHeaderCell>
                      <SubHeaderCell>Fecha</SubHeaderCell>
                      <SubHeaderCell align="center">Acciones</SubHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {archivos.map((archivo) => (
                      <TableRow key={archivo.id} hover>
                        <TableCell sx={{ fontSize: "0.78rem" }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <InsertDriveFileIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                            <a
                              href={`${ENV.API_BASE_URL}${archivo.archivo_url}`}
                              target="_blank"
                              rel="noreferrer"
                              style={{ color: "inherit" }}
                            >
                              {archivo.nombre_original}
                            </a>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.78rem" }}>{formatTamano(archivo.tamano)}</TableCell>
                        <TableCell sx={{ fontSize: "0.78rem" }}>{archivo.usuario || "—"}</TableCell>
                        <TableCell sx={{ fontSize: "0.78rem" }}>{formatDate(archivo.fecha)}</TableCell>
                        <TableCell align="center">
                          <Tooltip title="Eliminar archivo">
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleEliminarArchivo(archivo)}
                                disabled={deletingArchivoId === archivo.id}
                              >
                                {deletingArchivoId === archivo.id ? (
                                  <CircularProgress size={14} />
                                ) : (
                                  <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}

        {tab === 1 && (
        <>
        <Box sx={{ ...DIALOG_COMPACT_SX, mb: 1.5 }}>
          <Grid container spacing={1}>
            <Grid size={{ xs: 6, sm: 2 }}>
              <TextField select fullWidth size="small" label="Estado" name="estado"
                value={form.estado} onChange={handleFormChange} disabled={saving}>
                {ESTADOS_CONTRATO.map((estado) => (
                  <MenuItem key={estado} value={estado}>{estado}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 6, sm: 2 }}>
              <TextField fullWidth required size="small" type="number" label="Plazo mes" name="plazo_mes"
                value={form.plazo_mes} onChange={handleFormChange} disabled={saving}
                inputProps={{ min: 0 }} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField select fullWidth size="small" label="Garantia" name="garantia"
                value={form.garantia} onChange={handleFormChange} disabled={saving}>
                {GARANTIAS.map((garantia) => (
                  <MenuItem key={garantia} value={garantia}>{garantia}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 6, sm: 2 }}>
              <TextField fullWidth size="small" type="date" label="Inicio" name="fecha_inicio"
                value={form.fecha_inicio} onChange={handleFormChange} disabled={saving}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid size={{ xs: 6, sm: 2 }}>
              <TextField fullWidth size="small" type="date" label="Finalizacion" name="fecha_finalizacion"
                value={form.fecha_finalizacion} onChange={handleFormChange} disabled={saving}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid size={{ xs: 6, sm: 2 }}>
              <TextField select fullWidth size="small" label="Tacita" name="tasita"
                value={form.tasita} onChange={handleFormChange} disabled={saving}>
                <MenuItem value="No">No</MenuItem>
                <MenuItem value="Si">Si</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 6, sm: 2 }}>
              <TextField fullWidth size="small" type="date" label="Fecha tacita" name="fecha_tasita"
                value={form.fecha_tasita} onChange={handleFormChange} disabled={saving}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid size={{ xs: 6, sm: 2 }}>
              <TextField fullWidth size="small" label="Nro. letra" name="num_letra"
                value={form.num_letra} onChange={handleFormChange} disabled={saving} />
            </Grid>
            <Grid size={{ xs: 6, sm: 2 }}>
              <TextField fullWidth size="small" label="Monto letra" name="monto_letra"
                value={form.monto_letra} onChange={handleFormChange} disabled={saving} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField fullWidth size="small" label="Nombre letra" name="nombre_letra"
                value={form.nombre_letra} onChange={handleFormChange} disabled={saving} />
            </Grid>
            <Grid size={{ xs: 6, sm: 2 }}>
              <TextField fullWidth size="small" type="date" label="Fecha letra" name="fecha_letra"
                value={form.fecha_letra} onChange={handleFormChange} disabled={saving}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            {precioFields.length > 0 && (
              <Grid size={{ xs: 12 }}>
                <Divider sx={{ my: 0.5 }} />
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Precios {tipoServicio}
                </Typography>
              </Grid>
            )}
            {precioFields.map(([name, label]) => (
              <Grid key={name} size={{ xs: 12, sm: tipoServicio === "AVL" ? 3 : 2 }}>
                <TextField fullWidth size="small" type="number" label={label} name={name}
                  value={form[name]} onChange={handleFormChange} disabled={saving}
                  inputProps={{ min: 0, step: "0.01" }} />
              </Grid>
            ))}
            <Grid size={{ xs: 12, sm: 9 }}>
              <TextField fullWidth size="small" label="Observacion" name="observacion"
                value={form.observacion} onChange={handleFormChange} disabled={saving} />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }} sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
              <Button variant="outlined" size="small" onClick={handleNew} disabled={saving}
                startIcon={<AddIcon />} sx={{ minHeight: 32 }}>
                Nuevo
              </Button>
              <Button variant="contained" size="small" onClick={handleSaveContrato} disabled={saving}
                startIcon={saving ? <CircularProgress size={14} /> : null} sx={{ minHeight: 32 }}>
                {editing ? "Actualizar" : "Guardar"}
              </Button>
            </Grid>
          </Grid>
          {formError && <Alert severity="error" sx={{ mt: 1.5 }}>{formError}</Alert>}
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">Cargando contratos...</Typography>
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : !contratos.length ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            Sin contratos registrados para este servicio
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <SubHeaderCell>ID</SubHeaderCell>
                  <SubHeaderCell>Estado</SubHeaderCell>
                  <SubHeaderCell>Plazo</SubHeaderCell>
                  <SubHeaderCell>Garantia</SubHeaderCell>
                  <SubHeaderCell>Inicio</SubHeaderCell>
                  <SubHeaderCell>Finalizacion</SubHeaderCell>
                  <SubHeaderCell>Tacita</SubHeaderCell>
                  <SubHeaderCell>Observacion</SubHeaderCell>
                  <SubHeaderCell align="center">Acciones</SubHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {contratos.map((contrato) => {
                  const vigente = contrato.estado === "Vigente";

                  return (
                    <TableRow key={contrato.id_contrato} hover>
                      <TableCell sx={{ fontSize: "0.78rem" }}>{contrato.id_contrato}</TableCell>
                      <TableCell>
                        <Chip
                          label={contrato.estado || "—"}
                          size="small"
                          color={vigente ? "success" : "default"}
                          sx={{ fontSize: "0.68rem", height: 18 }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.78rem" }}>{contrato.plazo_mes || "—"}</TableCell>
                      <TableCell sx={{ fontSize: "0.78rem" }}>
                        <TruncatedCellText value={contrato.garantia} maxWidth={180} />
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.78rem" }}>{formatDate(contrato.fecha_inicio)}</TableCell>
                      <TableCell sx={{ fontSize: "0.78rem" }}>{formatDate(contrato.fecha_finalizacion)}</TableCell>
                      <TableCell sx={{ fontSize: "0.78rem" }}>{contrato.tasita || "—"}</TableCell>
                      <TableCell sx={{ fontSize: "0.78rem" }}>
                        <TruncatedCellText value={contrato.observacion} maxWidth={240} />
                      </TableCell>
                      <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                        <Tooltip title={vigente ? "Editar contrato" : "Solo contratos vigentes"}>
                          <span>
                            <IconButton size="small" onClick={() => handleEdit(contrato)} disabled={!vigente}>
                              <EditIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={vigente ? "Finalizar contrato" : "Solo contratos vigentes"}>
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleFinalizar(contrato)}
                              disabled={!vigente || finishingId === contrato.id_contrato}
                            >
                              {finishingId === contrato.id_contrato ? (
                                <CircularProgress size={14} />
                              ) : (
                                <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        </>
        )}
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContratosServicioDialog;
